<template>
  <v-layout class="catalog-settings-container">
    <v-container fluid>
      <v-card>
        <v-card-title>{{ $t('restaurant.menu.menu.settings') }}</v-card-title>
        <!--<v-card-subtitle>
        </v-card-subtitle>-->
        <v-card-text>
          <template v-if="!settings">
            <v-skeleton-loader type="sentences" />
          </template>
          <template v-else>
            <v-form @submit.prevent v-model="valid">
              <v-row>
                <v-col cols="12" md="6">
                  <h4>Opciones de visualización</h4>
                  <v-checkbox
                    v-model="settings.showDecimals"
                    label="Mostrar siempre los decimales"
                    hint="Active esta opción para que siempre se muestren decimales (ej.: $10 se mostrará como $10.00)"
                    persistent-hint
                  />
                  <v-checkbox
                    v-model="settings.hideSymbol"
                    label="Ocultar símbolo monetario"
                    hint="Active esta opción para ocultar el simbolo de la moneda (ej.: $15 se mostrará como 15)"
                    persistent-hint
                  />
                  <v-checkbox
                    v-model="settings.variantsFullPrice"
                    label="Mostrar precios múltiples completos"
                    hint="Solo para el Punto de Venta: muestra el precio completo de cada opción en lugar de la diferencia (ej.: +$5, -$20). En su carta online siempre se ven los precios completos."
                    persistent-hint
                  />
                  <v-checkbox
                    v-model="settings.taFromSeat"
                    label="Permitir pedidos take away desde un QR de mesa"
                    hint="Si usa menús múltiples, esta opción también ocultará el menú de take away desde los QR de mesa"
                    persistent-hint
                  />
                  <v-checkbox
                    v-model="settings.showDisabledItems"
                    label="Mostrar productos desactivados en la carta"
                    hint="Los productos y categorías desactivados se mostrarán en gris como 'No disponible' al final de la carta, en lugar de ocultarse."
                    persistent-hint
                  />
                  <div class="tw-flex tw-items-center tw-space-x-2 tw-mt-8">
                    <h4 class="mt-0">Idiomas</h4>
                    <RestaurantTierChip :tier="1" />
                  </div>
                  <template v-if="effectiveTier < 1">
                    <p class="grey--text">
                      La posibilidad de mostrar el menú en diferentes idiomas
                      solo está disponible en el plan
                      <v-chip label small color="orange" text-color="white"
                        >Plus</v-chip
                      >
                    </p>
                  </template>
                  <template v-else>
                    <v-select
                      v-model="menuLanguageId"
                      :items="languages"
                      item-text="name"
                      item-value="id"
                      class="mt-3"
                      style="width: 300px"
                      outlined
                      dense
                      persistent-hint
                      label="Idioma por defecto del menú"
                      hint="¿En qué idioma ingresó los items y categorías?"
                    />
                    <p class="mt-1 mb-0">
                      Configure los idiomas en los que se muestra su menú
                    </p>
                    <div class="tw-flex tw-items-center tw-space-x-2 my-2">
                      <v-checkbox
                        :input-value="menuLanguageIds !== null"
                        class="mt-0 pt-0"
                        label="Traducir el menú a otros idiomas"
                        @change="toggleTranslate"
                        hide-details
                      />
                      <IAChip />
                      <RestaurantTierChip :tier="1" />
                    </div>
                    <p v-if="menuLanguageIds !== null" class="mb-3">
                      <b>Importante:</b> El sistema de traducción por IA es automático y puede no ser 100% preciso, puede ajustar las traducciones manualmente en la sección de categorías y items.
                      <br/>
                      El sistema de traducciones corre una vez por hora, si no ve los cambios inmediatamente, por favor espere unos minutos.
                    </p>
                    <v-select
                      v-if="menuLanguageIds !== null"
                      v-model="menuLanguageIds"
                      class="mt-2"
                      :items="availableLanguages"
                      :menu-props="{ offsetY: true }"
                      item-text="name"
                      item-value="id"
                      outlined
                      deletable-chips
                      chips
                      label="Idiomas a los que traducir el menú"
                      multiple
                      hide-details
                    ></v-select>
                  </template>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="tw-flex tw-items-center tw-space-x-2">
                    <h4>Menú según tipo de pedido</h4>
                    <RestaurantTierChip :tier="2" />
                  </div>
                  <p class="grey--text mb-1">
                    ¿Usa el mismo menú para pedidos a la mesa, take away y
                    delivery?
                  </p>
                  <template v-if="effectiveTier < 2">
                    <p class="grey--text">
                      La posibilidad de tener diferentes menús según tipo de
                      pedido solo está disponible en el plan
                      <v-chip label small color="#ff6f00" text-color="white"
                        >Pro</v-chip
                      >
                    </p>
                  </template>
                  <template v-else>
                    <div class="outline-radio-group children-inline">
                      <v-radio-group
                        row
                        hide-details
                        v-model="settings.multipleMenus"
                        class="mt-0"
                      >
                        <div
                          class="outline-radio__item"
                          :class="{ active: !settings.multipleMenus }"
                          @click="settings.multipleMenus = false"
                        >
                          <v-radio
                            class="d-inline-flex input--label-left input--label-width-auto mr-0"
                            :value="false"
                            label="Menú único para todos"
                          />
                        </div>
                        <div
                          class="outline-radio__item"
                          :class="{ active: settings.multipleMenus }"
                          @click="settings.multipleMenus = true"
                        >
                          <v-radio
                            class="d-inline-flex input--label-left input--label-width-auto mr-0"
                            :value="true"
                            label="Menús diferentes para cada tipo"
                          />
                        </div>
                      </v-radio-group>
                    </div>
                    <template v-if="settings.multipleMenus">
                      <div class="menus-container">
                        <h4 class="font-weight-regular mb-1 mt-n1 font-size-9e">
                          Menús disponibles
                        </h4>
                        <div class="inner children-inline d-flex tw-flex-wrap">
                          <div class="menu-item">
                            <span class="name">{{ defaultMenuTitle }}</span>
                          </div>
                          <div
                            class="menu-item"
                            v-for="menu of menus"
                            :key="menu.id"
                          >
                            <span class="name">{{ menu.name }}</span>
                            <v-btn
                              icon
                              color="#ffe0c8"
                              x-small
                              @click="edit(menu)"
                              ><v-icon small>mdi-pencil</v-icon></v-btn
                            >
                          </div>
                          <v-btn class="add-new" icon @click="createNewMenu">
                            <v-icon>add</v-icon>
                          </v-btn>
                        </div>
                      </div>
                      <v-row>
                        <v-col
                          cols="12"
                          sm="4"
                          v-for="type of menuTypes"
                          :key="type.settingName"
                        >
                          <div class="order-menu">
                            <h4
                              class="font-weight-regular mb-1 mt-n1 font-size-9e"
                            >
                              {{ type.title }}
                            </h4>
                            <v-select
                              class="all-white white--text v-select--no-black"
                              :items="menuOptions"
                              v-model="settings[type.settingName]"
                              hide-details
                              background-color="#e48b46"
                              dark
                              color="#fff"
                              item-value="id"
                              solo
                              flat
                              dense
                              item-text="name"
                            ></v-select>
                          </div>
                        </v-col>
                      </v-row>
                    </template>
                  </template>
                </v-col>
              </v-row>
            </v-form @submit.prevent>
            <div class="actions text-right">
              <v-btn color="primary" @click="save" :loading="loading">{{
                $t('save')
              }}</v-btn>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-container>
  </v-layout>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import RestaurantsService, {
    IRestaurantSettings,
  } from '../../api/services/restaurants.service';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import MenusService from '../../api/services/menus.service';
  import { IMenu } from '../../interfaces/IMenu';
  import NewCategoryModal from '../modals/NewCategoryModal.vue';
  import { ICategory } from '../../interfaces/ICategory';
  import NewMenuModal from '../modals/NewMenuModal.vue';
  import { Globals } from '../../modules/Globals';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import RestaurantTierChip from '../partials/RestaurantTierChip.vue';
  import IAChip from '../partials/IAChip.vue';

  @Component({
    components: {
      RestaurantTierChip,
      IAChip,
    },
  })
  export default class CatalogSettings extends Vue {
    restaurantsService = new RestaurantsService();
    menusService = new MenusService();
    @Prop() restaurant: IRestaurant;
    settings: IRestaurantSettings = null;
    menus: IMenu[] = null;
    valid = false;
    loading = false;
    defaultMenuTitle = 'Menú por defecto';
    menuTypes = [
      { settingName: 'menu1Id', title: 'A la mesa' },
      { settingName: 'menu2Id', title: 'Take away' },
      { settingName: 'menu3Id', title: 'Delivery' },
    ];
    languages = Globals.languages;
    menuLanguageId = null;
    menuLanguageIds = null;

    async created() {
      this.menuLanguageId = this.restaurant.languageId;
      this.menuLanguageIds = this.restaurant.languageIds;

      await this.loadMenus();
      await this.loadSettings();
    }

    async loadSettings() {
      this.settings = await this.restaurantsService.getSettings(
        this.restaurant.id
      );
    }

    async loadMenus() {
      this.menus = await this.menusService.getAll(this.restaurant.id);
    }

    async saveSettings() {
      await this.restaurantsService.saveSettings(this.restaurant.id, {
        ...this.settings,
        languageId: this.menuLanguageId,
        languageIds: this.menuLanguageIds,
      });
      vueData.restaurant.languageId = this.menuLanguageId;
      vueData.restaurant.languageIds = this.menuLanguageIds;
      vueData.updateLanguages();
    }

    get effectiveTier(): number {
      return getEffectiveTier(this.restaurant);
    }

    get availableLanguages() {
      return this.languages.filter((l) => l.id !== this.menuLanguageId);
    }

    toggleTranslate() {
      if (this.menuLanguageIds === null) this.menuLanguageIds = [];
      else this.menuLanguageIds = null;
    }

    get menuOptions() {
      return [{ id: null, name: this.defaultMenuTitle }, ...this.menus];
    }

    async save() {
      if (!this.valid) {
        vueData.addAlert(
          'Asegurese de que todos los datos esten correctos',
          AlertType.Error
        );
        return;
      }
      if (this.menuLanguageIds && this.menuLanguageIds.length === 0) {
        vueData.addAlert('Debe elegir al menos un idioma', AlertType.Error);
        return;
      }
      try {
        await this.saveSettings();
        vueData.addAlert(this.$tc('storeSettings.saved'), AlertType.Success);
      } catch (e) {
        console.error(e);
        vueData.addAlert(
          this.$tc('storeSettings.errorSaving'),
          AlertType.Error
        );
      }
    }

    createNewMenu() {
      this.$modal.show(
        NewMenuModal,
        {
          restaurant: this.restaurant,
          menuOptions: this.menuOptions,
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadMenus();
          },
        }
      );
    }

    edit(menu: IMenu) {
      this.$modal.show(
        NewMenuModal,
        {
          menu,
        },
        {
          name: 'menu',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadMenus();
          },
          deleted: () => {
            this.menus.splice(this.menus.indexOf(menu), 1);
            this.$modal.hide('menu');
          },
        }
      );
    }
  }
</script>
